import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import { useSelector } from "react-redux";
import WebService from "../Services/WebService";
import CrossButton from "../Customs/Button/CrossButton";
import { AccessItem } from "../Models/Models";
import type { RootState } from "../Services/Store";
import styles from "./StockStatement.module.css";

interface StatementRow {
  item_id: number;
  item_type: string;
  stock_type_label?: string;
  item_name?: string;
  item_code?: string;
  category?: string;
  unit_of_measurement?: string;
  opening_stock?: number | string;
  stock_purchased?: number | string;
  stock_added?: number | string;
  stock_consumed?: number | string;
  stock_sold?: number | string;
  stock_adjustments?: number | string;
  current_stock?: number | string;
  reserved_quantity?: number | string;
  available_quantity?: number | string;
}

interface MovementRow {
  id?: number;
  created_at?: string;
  order_type_label?: string;
  source?: string;
  previous_quantity?: number | string;
  quantity_change?: number | string;
  new_quantity?: number | string;
  reference_number?: string;
  reason_label?: string;
  additional_note?: string;
  created_by_name?: string;
}

interface Totals {
  item_count?: number;
  opening_stock?: number | string;
  stock_purchased?: number | string;
  stock_added?: number | string;
  stock_consumed?: number | string;
  stock_sold?: number | string;
  stock_adjustments?: number | string;
  current_stock?: number | string;
  reserved_quantity?: number | string;
  available_quantity?: number | string;
}

const PAGE_SIZE = 20;
const MOVEMENT_PAGE_SIZE = 20;

function formatQty(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "0";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("en-IN", { maximumFractionDigits: 3 });
}

function formatDate(value?: string): string {
  if (!value) return "—";
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (ymd) {
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-IN");
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN");
}

function itemHref(row: StatementRow): string {
  return row.item_type === "RAW_MATERIAL"
    ? `/raw-material/${row.item_id}`
    : `/inventory/product-design/${row.item_id}`;
}

interface CategoryOption {
  category_id: number;
  category_name: string;
  item_type: "PRODUCT" | "RAW_MATERIAL";
}

function extractCategories(res: any, itemType: "PRODUCT" | "RAW_MATERIAL"): CategoryOption[] {
  const raw = Array.isArray(res?.data)
    ? res.data
    : Array.isArray(res?.data?.data)
    ? res.data.data
    : Array.isArray(res)
    ? res
    : [];
  return raw
    .filter((c: any) => c != null && c.category_id != null)
    .map((c: any) => ({
      category_id: Number(c.category_id),
      category_name: String(c.category_name || "Untitled"),
      item_type: itemType,
    }))
    .sort((a: CategoryOption, b: CategoryOption) =>
      a.category_name.localeCompare(b.category_name, undefined, { sensitivity: "base" })
    );
}

function categoryValue(cat: CategoryOption): string {
  return `${cat.item_type}-${cat.category_id}`;
}

function rowKey(row: StatementRow): string {
  return `${row.item_type}-${row.item_id}`;
}

const StockStatement: React.FC = () => {
  const accessList = useSelector((state: RootState) => state.auth.access || []);
  const inventoryAccess =
    (accessList.find((a: AccessItem) => a.code === "inventory_management") as AccessItem) ||
    ({} as AccessItem);
  const canRead = !!inventoryAccess.read;

  const [rows, setRows] = useState<StatementRow[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [stockType, setStockType] = useState("");
  const [categoryKey, setCategoryKey] = useState("");
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);
  const loadingRef = useRef(false);

  const [selected, setSelected] = useState<StatementRow | null>(null);
  const [movements, setMovements] = useState<MovementRow[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [movementPage, setMovementPage] = useState(1);
  const [movementHasMore, setMovementHasMore] = useState(true);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const movementSentinelRef = useRef<HTMLTableRowElement | null>(null);
  const movementsLoadingRef = useRef(false);

  const fetchStatement = useCallback(
    async (pageNo: number, like: string, type: string, category: string) => {
      if (!canRead) return;
      if (loadingRef.current && pageNo > 1) return;
      loadingRef.current = true;
      setLoading(true);
      setError("");
      const query = new URLSearchParams();
      query.set("page_number", String(pageNo));
      query.set("limit", String(PAGE_SIZE));
      if (like.trim()) query.set("like", like.trim());
      const [categoryType, categoryId] = category.split("-");
      if (type) query.set("item_type", type);
      else if (categoryType === "PRODUCT" || categoryType === "RAW_MATERIAL") {
        query.set("item_type", categoryType);
      }
      if (categoryId) query.set("category_id", categoryId);
      try {
        const res = await WebService.getAPI<any>({
          action: `/v1/door-management/stock-statement?${query.toString()}`,
        });
        const data = res?.data ?? {};
        const pageItems: StatementRow[] = Array.isArray(data.items) ? data.items : [];
        const pg = data.pagination ?? {};
        setTotals(data.totals ?? null);
        setRows((prev) => {
          const next = pageNo === 1 ? pageItems : [...prev, ...pageItems];
          const seen = new Set<string>();
          return next.filter((row) => {
            const key = rowKey(row);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        });
        if (pg.current_page != null && pg.total_pages != null) {
          setHasMore(pg.current_page < pg.total_pages);
        } else {
          setHasMore(pageItems.length >= PAGE_SIZE);
        }
      } catch {
        if (pageNo === 1) {
          setRows([]);
          setTotals(null);
        }
        setError("Failed to load stock statement.");
        setHasMore(false);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [canRead]
  );

  useEffect(() => {
    Promise.all([
      WebService.getAPI<any>({ action: "/v1/door-management/product/categories" }),
      WebService.getAPI<any>({ action: "/v1/door-management/raw-material/categories" }),
    ])
      .then(([productRes, rmRes]) => {
        setCategories([
          ...extractCategories(productRes, "PRODUCT"),
          ...extractCategories(rmRes, "RAW_MATERIAL"),
        ]);
      })
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setRows([]);
    fetchStatement(1, search, stockType, categoryKey);
  }, [fetchStatement, search, stockType, categoryKey]);

  useEffect(() => {
    if (page > 1) fetchStatement(page, search, stockType, categoryKey);
  }, [page, fetchStatement, search, stockType, categoryKey]);

  useEffect(() => {
    const node = sentinelRef.current;
    const root = tableWrapRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading && !loadingRef.current) {
          setPage((p) => p + 1);
        }
      },
      { root: root ?? null, rootMargin: "240px", threshold: 0 }
    );
    obs.observe(node);
    return () => {
      obs.unobserve(node);
      obs.disconnect();
    };
  }, [hasMore, loading, rows.length]);

  const fetchMovements = useCallback(async (row: StatementRow, pageNo: number) => {
    if (movementsLoadingRef.current && pageNo > 1) return;
    movementsLoadingRef.current = true;
    setMovementsLoading(true);
    const query = new URLSearchParams();
    query.set("page_number", String(pageNo));
    query.set("limit", String(MOVEMENT_PAGE_SIZE));
    try {
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/stock-statement/${row.item_type}/${row.item_id}?${query.toString()}`,
      });
      const data = res?.data ?? {};
      const pageItems: MovementRow[] = Array.isArray(data.movements) ? data.movements : [];
      const pg = data.pagination ?? {};
      setMovements((prev) => {
        const next = pageNo === 1 ? pageItems : [...prev, ...pageItems];
        const seen = new Set<string>();
        return next.filter((m, idx) => {
          const key = String(m.id ?? `m-${idx}-${m.created_at}`);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      });
      if (pg.current_page != null && pg.total_pages != null) {
        setMovementHasMore(pg.current_page < pg.total_pages);
      } else {
        setMovementHasMore(pageItems.length >= MOVEMENT_PAGE_SIZE);
      }
    } catch {
      if (pageNo === 1) setMovements([]);
      setMovementHasMore(false);
    } finally {
      movementsLoadingRef.current = false;
      setMovementsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (movementPage > 1 && selected) fetchMovements(selected, movementPage);
  }, [movementPage, selected, fetchMovements]);

  useEffect(() => {
    const node = movementSentinelRef.current;
    const root = panelRef.current;
    if (!node || !root || !selected) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && movementHasMore && !movementsLoading && !movementsLoadingRef.current) {
          setMovementPage((p) => p + 1);
        }
      },
      { root, rootMargin: "160px", threshold: 0 }
    );
    obs.observe(node);
    return () => {
      obs.unobserve(node);
      obs.disconnect();
    };
  }, [movementHasMore, movementsLoading, movements.length, selected]);

  const openItem = (row: StatementRow) => {
    setSelected(row);
    setMovements([]);
    setMovementHasMore(true);
    setMovementPage(1);
    fetchMovements(row, 1);
  };

  if (!canRead) {
    return (
      <div className={styles.page}>
        <div className={styles.denied}>You do not have permission to view the stock statement.</div>
      </div>
    );
  }

  const showInitialLoader = loading && rows.length === 0;

  const productCategories = categories.filter((c) => c.item_type === "PRODUCT");
  const rawMaterialCategories = categories.filter((c) => c.item_type === "RAW_MATERIAL");
  const visibleProductCategories = stockType === "RAW_MATERIAL" ? [] : productCategories;
  const visibleRawMaterialCategories = stockType === "PRODUCT" ? [] : rawMaterialCategories;

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <h1 className={styles.title}>Stock Statement</h1>
      </div>
      <div className={styles.filterRow}>
        <form
          className={styles.searchForm}
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchInput.trim());
          }}
        >
          <FaSearch className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search by name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </form>
        <select
          className={styles.filter}
          value={stockType}
          onChange={(e) => {
            setStockType(e.target.value);
            setCategoryKey("");
          }}
        >
          <option value="">All stock types</option>
          <option value="PRODUCT">Product</option>
          <option value="RAW_MATERIAL">Raw Material</option>
        </select>
        <select
          className={styles.filter}
          value={categoryKey}
          onChange={(e) => setCategoryKey(e.target.value)}
        >
          <option value="">All categories</option>
          {visibleProductCategories.length > 0 && visibleRawMaterialCategories.length > 0 ? (
            <>
              <optgroup label="Product">
                {visibleProductCategories.map((cat) => (
                  <option key={categoryValue(cat)} value={categoryValue(cat)}>
                    {cat.category_name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Raw Material">
                {visibleRawMaterialCategories.map((cat) => (
                  <option key={categoryValue(cat)} value={categoryValue(cat)}>
                    {cat.category_name}
                  </option>
                ))}
              </optgroup>
            </>
          ) : (
            [...visibleProductCategories, ...visibleRawMaterialCategories].map((cat) => (
              <option key={categoryValue(cat)} value={categoryValue(cat)}>
                {cat.category_name}
              </option>
            ))
          )}
        </select>
      </div>

      <div className={styles.tableWrap} ref={tableWrapRef}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Item / Product Name</th>
              <th>Stock Type</th>
              <th>Purchased / Received</th>
              <th>Stock Added</th>
              <th>Sold / Issued</th>
              <th>Adjustments</th>
              <th>Closing / Current</th>
              <th>Available</th>
            </tr>
          </thead>
          <tbody>
            {showInitialLoader ? (
              <tr>
                <td colSpan={8} className={styles.center}>Loading stock statement...</td>
              </tr>
            ) : error && rows.length === 0 ? (
              <tr>
                <td colSpan={8} className={styles.error}>{error}</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className={styles.center}>No stock items found.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={rowKey(row)} onClick={() => openItem(row)}>
                  <td>{row.item_name || "—"}</td>
                  <td>{row.stock_type_label || row.item_type}</td>
                  <td>{formatQty(row.stock_purchased)}</td>
                  <td>{formatQty(row.stock_added)}</td>
                  <td>{formatQty(row.stock_sold)}</td>
                  <td>{formatQty(row.stock_adjustments)}</td>
                  <td>{formatQty(row.current_stock)}</td>
                  <td>{formatQty(row.available_quantity)}</td>
                </tr>
              ))
            )}
            {rows.length > 0 && (
              <tr ref={sentinelRef} className={styles.sentinel}>
                <td colSpan={8}>
                  {loading ? "Loading more..." : hasMore ? "" : "End of list"}
                </td>
              </tr>
            )}
            {totals && rows.length > 0 && (
              <tr className={styles.totals}>
                <td colSpan={2}>Totals ({totals.item_count ?? 0} items)</td>
                <td>{formatQty(totals.stock_purchased)}</td>
                <td>{formatQty(totals.stock_added)}</td>
                <td>{formatQty(totals.stock_sold)}</td>
                <td>{formatQty(totals.stock_adjustments)}</td>
                <td>{formatQty(totals.current_stock)}</td>
                <td>{formatQty(totals.available_quantity)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className={styles.overlay} onClick={() => setSelected(null)}>
          <div
            className={styles.panel}
            ref={panelRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.panelHeader}>
              <div>
                <div className={styles.panelTitle}>{selected.item_name}</div>
                <div className={styles.panelSub}>{selected.stock_type_label || selected.item_type}</div>
              </div>
              <CrossButton onClick={() => setSelected(null)} />
            </div>
            <div className={styles.panelActions}>
              <Link className={styles.itemLink} to={itemHref(selected)}>
                Open item record
              </Link>
            </div>
            {movementsLoading && movements.length === 0 ? (
              <div className={styles.center}>Loading movements...</div>
            ) : movements.length === 0 ? (
              <div className={styles.center}>No stock movements recorded for this item.</div>
            ) : (
              <div className={styles.movementWrap}>
                <table className={styles.movementTable}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Source</th>
                      <th>Previous Qty</th>
                      <th>Change</th>
                      <th>New Qty</th>
                      <th>Reference</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.map((m, idx) => (
                      <tr key={`${m.id ?? "m"}-${idx}`}>
                        <td>{formatDate(m.created_at)}</td>
                        <td>{m.source || m.order_type_label || "—"}</td>
                        <td>{formatQty(m.previous_quantity)}</td>
                        <td>{formatQty(m.quantity_change)}</td>
                        <td>{formatQty(m.new_quantity)}</td>
                        <td>{m.reference_number || "—"}</td>
                        <td>{m.reason_label || m.additional_note || "—"}</td>
                      </tr>
                    ))}
                    <tr ref={movementSentinelRef} className={styles.sentinel}>
                      <td colSpan={7}>
                        {movementsLoading ? "Loading more..." : movementHasMore ? "" : "End of movements"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StockStatement;
